import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, MapPin } from "@/lib/public-icons";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useSession } from "@/lib/host/auth-client";
import { endSession } from "@/lib/session/sign-out";
import { ChangePassword } from "./-components/change-password";
import { PortalShell, type PortalBrand } from "./-components/portal-shell";

/** One building's branding when every application is theirs; otherwise the portal is plainly
 * ours. */
function brandOf(rows: Array<{ facilityName: string | null; accentColor: string | null }>) {
  const [first] = rows;
  if (!first) return null;
  if (!rows.every((row) => row.facilityName === first.facilityName)) return null;
  const brand: PortalBrand = { name: first.facilityName, accentColor: first.accentColor };
  return brand;
}

export const Route = createFileRoute("/applicant-portal/")({
  component: ApplicantPortal,
});

/** Everything somebody has applied for, and where each one has got to. */
function ApplicantPortal() {
  const session = useSession();
  const queryClient = useQueryClient();
  const applications = useQuery({
    ...orpc.applicantPortal.applications.queryOptions({}),
    retry: false,
    enabled: Boolean(session.data),
  });

  if (session.isPending) {
    return (
      <PortalShell>
        <Skeleton className="h-40 w-full" />
      </PortalShell>
    );
  }

  if (!session.data) {
    return (
      <PortalShell>
        <PortalNotice
          icon={<Briefcase />}
          title="Sign in to see your applications"
          action={
            <Button asChild size="lg">
              <Link to="/login" search={{ next: "/applicant-portal" }}>
                Sign in
              </Link>
            </Button>
          }
          secondary={
            <Button asChild variant="link">
              <Link to="/forgot-password">Send me a link</Link>
            </Button>
          }
        >
          Use the email address you applied with. If you have not set a password yet, ask for a link
          and we will send you one.
        </PortalNotice>
      </PortalShell>
    );
  }

  return (
    <PortalShell
      name={session.data.user.name}
      brand={brandOf(applications.data ?? [])}
      onSignOut={() => void endSession(queryClient)}
    >
      <div className="mb-6">
        <PageHeader title="Your applications" actions={<ChangePassword />} />
      </div>

      {applications.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : applications.isError && applications.data === undefined ? (
        <ReadFailed
          message="Could not load your applications."
          error={applications.error}
          retrying={applications.isFetching}
          onRetry={() => void applications.refetch()}
        />
      ) : (applications.data ?? []).length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          description="When you apply for a position it will show up here, with where it has got to."
        />
      ) : (
        <div className="space-y-3">
          {(applications.data ?? []).map((application) => (
            <Link
              key={application.id}
              className="group focus-visible:ring-ring/80 block rounded-xl outline-hidden focus-visible:ring-[3px]"
              params={{ applicationId: application.id }}
              to="/applicant-portal/$applicationId"
            >
              <Card className="group-hover:bg-accent transition-colors">
                <CardContent>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold">{application.jobTitle}</h2>
                      <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        {application.facilityName ? <span>{application.facilityName}</span> : null}
                        {application.jobLocation ? (
                          <span className="flex items-center gap-1">
                            <MapPin className="size-3" />
                            {application.jobLocation}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Passing the screen opened this; it is not finished until
                          the form is sent, and the list must not read as done. */}
                      {application.jobApplicationOwed ? (
                        <Badge variant="warn">Job application not sent</Badge>
                      ) : null}
                      {application.documentsOwed > 0 ? (
                        <Badge variant="warn">
                          {application.documentsOwed === 1
                            ? "1 document to upload"
                            : `${String(application.documentsOwed)} documents to upload`}
                        </Badge>
                      ) : null}
                      {/* Opening the thread clears this, so it is only ever a
                          message the candidate has genuinely not seen. */}
                      {application.unreadMessages > 0 ? (
                        <Badge>
                          {application.unreadMessages === 1
                            ? "1 new message"
                            : `${application.unreadMessages} new messages`}
                        </Badge>
                      ) : null}
                      <Badge variant="neutral">{application.stageLabel}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </PortalShell>
  );
}
